import { z } from 'zod';

export const AddressLite = z.object({
  name: z.string(),
  email: z.string(),
});
export type AddressLite = z.infer<typeof AddressLite>;

export const ThreadRow = z.object({
  id: z.number().int(),
  accountId: z.number().int(),
  subject: z.string(),
  snippet: z.string(),
  participants: z.array(AddressLite),
  lastMessageAt: z.number().int(),
  messageCount: z.number().int(),
  unread: z.boolean(),
  starred: z.boolean(),
  hasAttachment: z.boolean(),
  snoozedUntil: z.number().int().nonnegative().nullable().default(null),
});
export type ThreadRow = z.infer<typeof ThreadRow>;

export const ThreadListResponse = z.object({
  rows: z.array(ThreadRow),
  nextCursor: z.string().nullable(),
});
export type ThreadListResponse = z.infer<typeof ThreadListResponse>;

/**
 * A bounded page used to build an encrypted device-side mail index. Unlike the
 * normal unified list, searchable-history pages can span every synchronized
 * non-Drafts folder while remaining user/account scoped on the server.
 */
export const OfflineMailIndexPage = ThreadListResponse.extend({
  total: z.number().int().nonnegative().nullable(),
});
export type OfflineMailIndexPage = z.infer<typeof OfflineMailIndexPage>;

export const MailSearchSort = z.enum(['relevance', 'newest']);
export type MailSearchSort = z.infer<typeof MailSearchSort>;

export const MailSearchMatch = z.object({
  messageId: z.number().int().positive(),
  date: z.number().int(),
  senderLabel: z.string(),
  subject: z.string(),
  snippet: z.string(),
  field: z.enum(['subject', 'sender', 'recipient', 'body']),
  isLatestMessage: z.boolean(),
});
export type MailSearchMatch = z.infer<typeof MailSearchMatch>;

/**
 * Search remains compatible with the virtualized thread row, but identifies
 * the concrete matching message so an older hit is never represented by an
 * unrelated latest-message snippet.
 */
export const SearchThreadRow = ThreadRow.extend({
  match: MailSearchMatch.nullable(),
});
export type SearchThreadRow = z.infer<typeof SearchThreadRow>;

export const SearchResponse = z.object({
  rows: z.array(SearchThreadRow),
  nextCursor: z.string().nullable(),
  sort: MailSearchSort,
});
export type SearchResponse = z.infer<typeof SearchResponse>;

export const AttachmentMeta = z.object({
  id: z.number().int(),
  filename: z.string().nullable(),
  contentType: z.string(),
  size: z.number().int(),
  isInline: z.boolean(),
});
export type AttachmentMeta = z.infer<typeof AttachmentMeta>;

export const MessageAuthenticationResult = z.enum([
  'pass',
  'fail',
  'softfail',
  'neutral',
  'none',
  'temperror',
  'permerror',
  'unknown',
]);
export type MessageAuthenticationResult = z.infer<
  typeof MessageAuthenticationResult
>;

export const MessageAuthentication = z.object({
  spf: MessageAuthenticationResult,
  dkim: MessageAuthenticationResult,
  dmarc: MessageAuthenticationResult,
  reportedBy: z.string().nullable(),
});
export type MessageAuthentication = z.infer<typeof MessageAuthentication>;

export const MessageUnsubscribe = z.object({
  httpsUrl: z.string().nullable(),
  oneClick: z.boolean(),
  mailto: z
    .object({
      address: z.string(),
      subject: z.string(),
      body: z.string(),
    })
    .nullable(),
});
export type MessageUnsubscribe = z.infer<typeof MessageUnsubscribe>;

export const MessageMetadata = z.object({
  listId: z.string().nullable(),
  authentication: MessageAuthentication.nullable(),
  unsubscribe: MessageUnsubscribe.nullable(),
});
export type MessageMetadata = z.infer<typeof MessageMetadata>;

export const MessageEnvelope = z.object({
  id: z.number().int(),
  accountId: z.number().int(),
  folderId: z.number().int(),
  from: z.array(AddressLite),
  to: z.array(AddressLite),
  cc: z.array(AddressLite),
  replyTo: z.array(AddressLite),
  subject: z.string(),
  date: z.number().int(),
  isRead: z.boolean(),
  isStarred: z.boolean(),
  isAnswered: z.boolean(),
  isDraft: z.boolean(),
  snippet: z.string(),
  bodyState: z.enum(['headers', 'full', 'unavailable']),
  hasAttachments: z.boolean(),
  attachments: z.array(AttachmentMeta),
  metadata: MessageMetadata.default({
    listId: null,
    authentication: null,
    unsubscribe: null,
  }),
});
export type MessageEnvelope = z.infer<typeof MessageEnvelope>;

export const ThreadDetail = z.object({
  id: z.number().int(),
  accountId: z.number().int(),
  subject: z.string(),
  snoozedUntil: z.number().int().nonnegative().nullable().default(null),
  messages: z.array(MessageEnvelope),
});
export type ThreadDetail = z.infer<typeof ThreadDetail>;

export const MessageBody = z.object({
  state: z.enum(['ready', 'pending']),
  // Default preserves compatibility with a response already cached by an
  // older client build while new servers always send the explicit hint.
  hasHtml: z.boolean().default(false),
  html: z.string().nullable(),
  text: z.string().nullable(),
  hasBlockedImages: z.boolean(),
  hasRemoteImages: z.boolean(),
  remoteImageSender: z.string().nullable(),
  imagePermission: z.enum(['blocked', 'once', 'sender']),
  imageMode: z.enum(['blocked', 'standard', 'compatibility', 'direct']),
});
export type MessageBody = z.infer<typeof MessageBody>;

/**
 * Browser thread opens carry the first visible message body when it is
 * already available locally. This removes the normal thread-then-body HTTP
 * waterfall without making every message in a long conversation part of the
 * initial response.
 */
export const ThreadOpenResponse = ThreadDetail.extend({
  initialBody: z
    .object({
      messageId: z.number().int().positive(),
      body: MessageBody,
    })
    .nullable(),
});
export type ThreadOpenResponse = z.infer<typeof ThreadOpenResponse>;

export const RemoteImageSenderPreference = z.object({
  senderEmail: z.string(),
  allowed: z.boolean(),
});
export type RemoteImageSenderPreference = z.infer<typeof RemoteImageSenderPreference>;

export const MessageAction = z.enum([
  'read',
  'unread',
  'star',
  'unstar',
  'archive',
  'trash',
  'spam',
  'not_spam',
  'move',
  'delete_perm',
]);
export type MessageAction = z.infer<typeof MessageAction>;

/**
 * A batch action targets either whole threads or explicit message copies.
 * sourceFolderId makes thread moves from non-Inbox folder views unambiguous and
 * confines permanent deletion to a specific Trash folder; archive/trash
 * otherwise use each account's special-use destination.
 */
export const MessageActionRequest = z
  .object({
    threadIds: z.array(z.number().int().positive()).min(1).max(500).optional(),
    messageIds: z.array(z.number().int().positive()).min(1).max(2_000).optional(),
    action: MessageAction,
    sourceFolderId: z.number().int().positive().optional(),
    targetFolderId: z.number().int().positive().optional(),
  })
  .superRefine((value, ctx) => {
    if (Boolean(value.threadIds) === Boolean(value.messageIds)) {
      ctx.addIssue({
        code: 'custom',
        message: 'provide exactly one of threadIds or messageIds',
        path: ['threadIds'],
      });
    }
    if (value.action === 'move' && value.targetFolderId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'targetFolderId is required for move',
        path: ['targetFolderId'],
      });
    }
    if (
      value.action === 'delete_perm' &&
      value.threadIds !== undefined &&
      value.sourceFolderId === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'sourceFolderId is required to permanently delete threads',
        path: ['sourceFolderId'],
      });
    }
  });
export type MessageActionRequest = z.infer<typeof MessageActionRequest>;

export const MessageActionUndo = z.object({
  requests: z.array(MessageActionRequest).min(1),
});
export type MessageActionUndo = z.infer<typeof MessageActionUndo>;

export const MessageActionResponse = z.object({
  ok: z.literal(true),
  affectedMessageIds: z.array(z.number().int().positive()),
  undo: MessageActionUndo.nullable(),
});
export type MessageActionResponse = z.infer<typeof MessageActionResponse>;

export const MAX_SEARCH_BULK_THREADS = 500;

/**
 * Search-wide actions resolve matching conversation ids on the server at the
 * moment the user confirms the action. Permanent deletion and named-folder
 * moves are deliberately unavailable because a unified search can span
 * non-Trash folders and multiple accounts.
 */
export const SearchMessageAction = z.enum([
  'read',
  'unread',
  'star',
  'unstar',
  'archive',
  'trash',
  'spam',
  'not_spam',
]);
export type SearchMessageAction = z.infer<typeof SearchMessageAction>;

export const SearchMessageActionRequest = z.object({
  q: z.string().trim().min(1).max(1_000),
  action: SearchMessageAction,
});
export type SearchMessageActionRequest = z.infer<typeof SearchMessageActionRequest>;

export const SearchMessageActionResponse = MessageActionResponse.extend({
  selectedThreadCount: z.number().int().min(0).max(MAX_SEARCH_BULK_THREADS),
  selectionLimit: z.literal(MAX_SEARCH_BULK_THREADS),
  hasMore: z.boolean(),
});
export type SearchMessageActionResponse = z.infer<typeof SearchMessageActionResponse>;

export const ThreadSnoozeRequest = z.object({
  threadIds: z
    .array(z.number().int().positive())
    .min(1)
    .max(500)
    .refine((ids) => new Set(ids).size === ids.length, 'threadIds must be unique'),
  until: z.number().int().nonnegative(),
});
export type ThreadSnoozeRequest = z.infer<typeof ThreadSnoozeRequest>;

export const ThreadUnsnoozeRequest = ThreadSnoozeRequest.pick({ threadIds: true });
export type ThreadUnsnoozeRequest = z.infer<typeof ThreadUnsnoozeRequest>;

export const ThreadSnoozeResponse = z.object({
  ok: z.literal(true),
  threadIds: z.array(z.number().int().positive()),
  snoozedUntil: z.number().int().nonnegative().nullable(),
});
export type ThreadSnoozeResponse = z.infer<typeof ThreadSnoozeResponse>;

export const EmptyTrashBatchResponse = z.object({
  ok: z.literal(true),
  folderId: z.number().int().positive(),
  deletedMessageCount: z.number().int().min(0).max(500),
  remainingMessageCount: z.number().int().nonnegative(),
});
export type EmptyTrashBatchResponse = z.infer<typeof EmptyTrashBatchResponse>;
